import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import "../css/checkout.scss";
import ReactFlagsSelect from "react-flags-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { BASE_URL } from "../constans/ApiEndpointConstants";
import { sendMessage } from "../apis/SendMessageAPI";
import { getUrl } from "../apis/GetPkAPI";
import { PixelWithEvent } from "./common/PixelWithEvent ";
import * as FacebookAPI from "../apis/FacebookAPI";
import Cookies from "js-cookie";
import * as GetPixelAPI from "../apis/GetPixelAPI";
import LocalStorageUtil from "../utils/localStorageUtil";
import Header from "./common/Header";
import Footer from "./common/Footer";
import { CartContext } from "./common/CartContext";
import { useTranslation } from "react-i18next";
import { search_product_by_id } from "../apis/SearchProductById";
import { login as apiLogin } from "../apis/LoginAPI";
import { modify_user } from "../apis/ModifyUser";
import {
  convertEurToGbp,
  isGbpLanguage,
  EUR_TO_GBP_RATE,
} from "../constans/CurrencyConstants";

/** 运费阈值与标准运费（EUR） */
const CHECKOUT_FREE_SHIP_THRESHOLD_EUR = 250;
const CHECKOUT_STANDARD_SHIPPING_EUR = 4.95;
const CHECKOUT_STANDARD_SHIPPING_GBP =
  EUR_TO_GBP_RATE > 0
    ? parseFloat((CHECKOUT_STANDARD_SHIPPING_EUR * EUR_TO_GBP_RATE).toFixed(2))
    : 4.26;

// 根据当前语言/货币计算运费展示 (全部使用 EUR)
const getShippingDisplay = (cartItems, language) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return {
      amount: 0,
      formatted: "€0.00",
      free: true,
    };
  }
  const sub = cartItems.reduce(
    (s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 0),
    0
  );
  if (sub >= CHECKOUT_FREE_SHIP_THRESHOLD_EUR) {
    return { amount: 0, formatted: "Free", free: true };
  }
  return {
    amount: CHECKOUT_STANDARD_SHIPPING_EUR,
    formatted: `€${CHECKOUT_STANDARD_SHIPPING_EUR.toFixed(2)}`,
    free: false,
  };
};

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const tr = (key, fallback) => {
    try {
      const v = t(key);
      return v === key ? fallback ?? key : v;
    } catch {
      return fallback ?? key;
    }
  };
  const { items: cartItems = [] } = useContext(CartContext) || {};
  const pkMessageSentRef = useRef(false);
  const iframeContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(720);
  const storedAd = LocalStorageUtil.getAd();
  const ad = storedAd ?? "PurityBase";
  const [pixelRes, setPixelRes] = useState(null);
  const [productPixelMap, setProductPixelMap] = useState({}); // { [productId]: { pixel, fbqtoken } }

  const [loginOpen, setLoginOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const [discountCode, setDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState("");

  const [paymentMethod, setPaymentMethod] = useState("Credit Card");
  const [agreements, setAgreements] = useState({
    research: false,
    nonPrivate: false,
    notMedicine: false,
    terms: false,
    offers: false,
  });
  const [agreementsError, setAgreementsError] = useState(false);

  //event_id，要去重，一天之内用一个，存cookie
  const getEventId = (eventType, auxiliaryProps) => {
    // 根据事件类型使用不同的cookie名
    let cookieName = `fb_event_id_${eventType}`;

    if (eventType === "CustomizeProduct") {
      if (auxiliaryProps) {
        cookieName = `fb_event_id_${eventType}_${auxiliaryProps}`;
      }
    }

    let storedEventId = Cookies.get(cookieName);
    if (storedEventId) {
      return storedEventId;
    } else {
      // 使用 10 位随机数字作为 event_id
      const newEventId = Math.round(Math.random() * 10000000000).toString();
      Cookies.set(cookieName, newEventId, { expires: 1 }); // 存储1天
      return newEventId;
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [shippingLoading, setShippingLoading] = useState(true);

  // 语言 -> 默认国家
  const getDefaultCountryByLang = (lang) => {
    const l = String(lang || "").toLowerCase();
    if (l === "uk" || l.startsWith("en")) return "GB";
    if (l.startsWith("nl")) return "NL";
    if (l.startsWith("de")) return "DE";
    if (l.startsWith("fr")) return "FR";
    return "DE";
  };

  const getCountryPhoneRule = (country) => {
    const c = String(country || "US").toUpperCase();
    const rules = {
      GB: { code: "44", min: 10, max: 11 },
      NL: { code: "31", min: 9, max: 10 },
      US: { code: "1", min: 10, max: 11 },
      DE: { code: "49", min: 10, max: 12 },
      FR: { code: "33", min: 9, max: 10 },
      BE: { code: "32", min: 8, max: 10 },
    };
    return rules[c] || { code: "", min: 7, max: 16 };
  };

  const isValidPhoneSimple = (value, country) => {
    if (!value) return true;
    const rule = getCountryPhoneRule(country);
    const digits = String(value).replace(/\D/g, "");
    if (!digits) return false;
    if (rule.code && digits.startsWith(rule.code)) {
      const national = digits.slice(rule.code.length);
      return national.length >= rule.min && national.length <= rule.max;
    }
    const stripped = digits.replace(/^0+/, "");
    return stripped.length >= rule.min && stripped.length <= rule.max;
  };

  useEffect(() => {
    const url = BASE_URL;
    const strippedUrl = url.replace(/^https?:\/\//, "");

    GetPixelAPI.getpixel(strippedUrl).then((res) => {
      const pixelApidata = res.data.data;
      setPixelRes(pixelApidata);
    });

    // 批量查询购物车每个商品的 pixel/fbqtoken（按商品 ID 查询）
    const cartProductIds = cartItems.map((it) => it.id).filter(Boolean);
    if (cartProductIds.length === 0) return;

    const uniqueIds = [...new Set(cartProductIds)];
    const requests = uniqueIds.map((pid) =>
      search_product_by_id(pid)
        .then((res) => {
          if (res.data?.code === 1 && res.data?.data) {
            return {
              id: pid,
              pixel: res.data.data.pixel,
              fbqtoken: res.data.data.fbqtoken,
            };
          }
          return null;
        })
        .catch(() => null)
    );

    Promise.all(requests).then((results) => {
      const map = {};
      results.forEach((r) => {
        if (r) map[r.id] = { pixel: r.pixel, fbqtoken: r.fbqtoken };
      });
      setProductPixelMap(map);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 随机生成六位字符（包含数字和字母）
  const generateRandomCode = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  };

  // 获取或生成 userid
  let userid = localStorage.getItem("userid");
  if (!userid) {
    userid = generateRandomCode();
    localStorage.setItem("userid", userid);
  }

  const [paymentInfo, setPaymentInfo] = useState(() => {
    const langDefaultCountry = getDefaultCountryByLang(i18n.language);
    // 优先从 orderDetail 读取之前保存的订单信息
    const savedOrderDetail = localStorage.getItem("orderDetail");
    if (savedOrderDetail) {
      try {
        const orderDetail = JSON.parse(savedOrderDetail);
        // 从 orderDetail 中提取用户信息
        return {
          email: orderDetail.email || "",
          firstName: orderDetail.firstName || "",
          lastName: orderDetail.lastName || "",
          zipCode: orderDetail.zipCode || "",
          state: orderDetail.state || "",
          city: orderDetail.city || "",
          address: orderDetail.address || "",
          phone: orderDetail.phone || "",
          country: orderDetail.country || langDefaultCountry,
          selected: orderDetail.country || langDefaultCountry,
        };
      } catch (error) {
        console.error("读取订单信息失败:", error);
      }
    }

    // 其次从 userPaymentInfo 读取之前保存的用户信息
    const savedUserInfo = localStorage.getItem("userPaymentInfo");
    if (savedUserInfo) {
      try {
        return JSON.parse(savedUserInfo);
      } catch (error) {
        console.error("读取用户信息失败:", error);
      }
    }

    // 如果没有保存的信息，返回默认值
    return {
      email: "",
      firstName: "",
      lastName: "",
      zipCode: "",
      state: "",
      city: "",
      address: "",
      phone: "",
      country: langDefaultCountry,
      selected: langDefaultCountry,
      houseNumber: "",
      houseNumberAddition: "",
    };
  });

  // 语言变更时同步国家/区号
  useEffect(() => {
    const target = getDefaultCountryByLang(i18n.language);
    setPaymentInfo((prev) => {
      if (prev.country === target && prev.selected === target) return prev;
      return { ...prev, country: target, selected: target };
    });
  }, [i18n.language]);
  // 是否由邮编自动填充了州/城市，如果是则不允许手动修改
  const [isLocationLocked, setIsLocationLocked] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({}); // 追踪哪些字段已经被触碰过
  const [formIsValid, setFormIsValid] = useState(false);
  const checkoutPageMessageSentRef = useRef(false);

  // 发送 Checkout Page 消息
  useEffect(() => {
    if (!checkoutPageMessageSentRef.current) {
      sendMessage(`${BASE_URL} [Checkout Page]:ad=${ad}:userid=${userid}`);

      checkoutPageMessageSentRef.current = true;
    }
  }, [ad, userid]);

  useEffect(() => {
    const { email, firstName, lastName, zipCode, state, city, address } =
      paymentInfo;

    // 仅必填字段：电话为可选
    const fieldsAreFilled =
      email && firstName && lastName && zipCode && state && city && address;

    const noErrors = Object.keys(errors).length === 0;
    
    // 检查必需的条款是否已勾选
    const agreementsChecked = agreements.research && agreements.nonPrivate && agreements.notMedicine && agreements.terms;
    
    const isValid = fieldsAreFilled && noErrors && agreementsChecked;

    setFormIsValid(isValid);
  }, [paymentInfo, errors, agreements]);

  // 当 iframe 显示时，自动滚动到 iframe 位置
  useEffect(() => {
    if (showIframe && iframeContainerRef.current) {
      setTimeout(() => {
        iframeContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 0);
    }
  }, [showIframe]);

  const validateEmail = (email) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    if (!emailRegex.test(email)) {
      return t("checkout.validation.email.invalid");
    }
    return "";
  };

  // Cookie 或 localStorage 存在用户信息时，直接显示已登录状态（头像+邮箱卡片）
  useEffect(() => {
    try {
      const token = Cookies.get("token");
      const username = Cookies.get("username");
      if (token && username) {
        setLoginEmail(String(username));
        setLoginSuccess(true);
        setLoginOpen(true);
        setLoginError(""); // 恢复已登录状态时清除之前的登录错误提示
        return;
      }
      const saved = localStorage.getItem("accountEmail");
      if (saved) {
        setLoginEmail(saved);
        setLoginSuccess(true);
        setLoginOpen(true);
        setLoginError("");
      }
    } catch {}
  }, []);

  const onLoginSubmit = (e) => {
    e && e.preventDefault && e.preventDefault();
    setLoginError("");
    const v = loginEmail.trim();
    const err = validateEmail(v);
    if (err) {
      setLoginError(t("checkout.login.errorUnknownEmail"));
      return;
    }
    if (!loginPassword) {
      setLoginError(t("checkout.login.errorPasswordRequired"));
      return;
    }
    setLoginLoading(true);
    apiLogin(v, loginPassword, null)
      .then((res) => {
        setLoginLoading(false);
        const ok =
          res &&
          res.status === 200 &&
          (res.data?.code === 1 || res.data?.success || res.data === true);
        if (ok) {
          const dataBlock = res?.data?.data || {};
          const tokenVal = dataBlock?.token;
          const userBlock = dataBlock?.user || res?.data?.user || {};
          const userIdVal = userBlock?.id;
          const roleTypeVal = userBlock?.roleType || userBlock?.role || "";
          const apiUsername =
            res?.data?.data?.user?.username ||
            res?.data?.user?.username ||
            res?.data?.username ||
            v;
          try {
            if (tokenVal) Cookies.set("token", String(tokenVal), { path: "/" });
            if (roleTypeVal)
              Cookies.set("roleType", String(roleTypeVal), { path: "/" });
            if (userIdVal != null)
              Cookies.set("userId", String(userIdVal), { path: "/" });
            if (apiUsername)
              Cookies.set("username", String(apiUsername), { path: "/" });
          } catch {}
          setLoginEmail(String(apiUsername));
          setLoginSuccess(true);
          setLoginError("");
          setLoginOpen(true);
          setMenuOpen(false);
        } else {
          const msg =
            res?.data?.message ||
            res?.data?.msg ||
            t("checkout.login.errorUnknownEmail");
          setLoginError(String(msg));
        }
      })
      .catch((err2) => {
        setLoginLoading(false);
        const msg =
          err2?.response?.data?.message ||
          err2?.message ||
          t("checkout.login.errorUnknownEmail");
        setLoginError(String(msg));
      });
  };

  const logoutUser = () => {
    try {
      Cookies.remove("token", { path: "/" });
    } catch {}
    try {
      Cookies.remove("roleType", { path: "/" });
    } catch {}
    try {
      Cookies.remove("username", { path: "/" });
    } catch {}
    try {
      Cookies.remove("userId", { path: "/" });
    } catch {}
    try {
      localStorage.removeItem("accountEmail");
    } catch {}
    setMenuOpen(false);
    setLoginSuccess(false);
    setRegSuccess(false);
    setLoginOpen(false);
    setRegisterOpen(false);
  };

  const onRegisterSubmit = (e) => {
    e && e.preventDefault && e.preventDefault();
    setRegError("");
    const emailVal = regEmail.trim();
    if (!emailVal) {
      setRegError(t("checkout.register.errors.emailRequired"));
      return;
    }
    const emailErr = validateEmail(emailVal);
    if (emailErr) {
      setRegError(t("checkout.register.errors.emailInvalid"));
      return;
    }
    if (!regPassword) {
      setRegError(t("checkout.register.errors.passwordRequired"));
      return;
    }
    setRegLoading(true);
    modify_user(
      null,
      emailVal,
      regPassword,
      null,
      null,
      null,
      "user",
      null,
      null
    )
      .then((res) => {
        setRegLoading(false);
        const ok =
          res &&
          res.status === 200 &&
          (res.data?.code === 1 || res.data?.success || res.data === true);
        if (ok) {
          setRegSuccess(true);
          setLoginEmail(emailVal);
          setRegLoading(true);
          return apiLogin(emailVal, regPassword, null)
            .then((r2) => {
              setRegLoading(false);
              const ok2 =
                r2 &&
                r2.status === 200 &&
                (r2.data?.code === 1 || r2.data?.success || r2.data === true);
              if (ok2) {
                const dataBlock2 = r2?.data?.data || {};
                const tokenVal2 = dataBlock2?.token;
                const userBlock2 = dataBlock2?.user || r2?.data?.user || {};
                const userIdVal2 = userBlock2?.id;
                const roleTypeVal2 =
                  userBlock2?.roleType || userBlock2?.role || "";
                const apiUsername2 =
                  r2?.data?.data?.user?.username ||
                  r2?.data?.user?.username ||
                  r2?.data?.username ||
                  emailVal;
                try {
                  if (tokenVal2)
                    Cookies.set("token", String(tokenVal2), { path: "/" });
                  if (roleTypeVal2)
                    Cookies.set("roleType", String(roleTypeVal2), {
                      path: "/",
                    });
                  if (userIdVal2 != null)
                    Cookies.set("userId", String(userIdVal2), { path: "/" });
                  if (apiUsername2)
                    Cookies.set("username", String(apiUsername2), {
                      path: "/",
                    });
                } catch {}
                setLoginEmail(String(apiUsername2));
                setLoginSuccess(true);
                setRegisterOpen(false);
                setLoginOpen(true);
                setMenuOpen(false);
                try {
                  localStorage.setItem("accountEmail", String(apiUsername2));
                } catch {}
                setLoginError(""); // 注册后自动登录成功，清除可能存在的旧登录错误
              } else {
                const msg2 =
                  r2?.data?.message || r2?.data?.msg || "Login failed";
                setRegError(String(msg2));
              }
            })
            .catch((e2) => {
              setRegLoading(false);
              const msg2 =
                e2?.response?.data?.message || e2?.message || "Login failed";
              setRegError(String(msg2));
            });
        } else {
          const msg =
            res?.data?.message || res?.data?.msg || "Registration failed";
          setRegError(String(msg));
        }
      })
      .catch((err2) => {
        setRegLoading(false);
        const msg =
          err2?.response?.data?.message ||
          err2?.message ||
          "Registration failed";
        setRegError(String(msg));
      });
  };

  //根据邮编自动填写城市和州
  const onPlzChange = (e) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/[^a-zA-Z0-9]/g, "");

    setPaymentInfo((prev) => ({
      ...prev,
      zipCode: sanitized,
    }));

    // 如果邮编长度足够，清除相关错误（城市和州需手动填写或通过其他方式填充）
    if (sanitized.length >= 4) {
      setIsLocationLocked(false);

      // 清空与 PLZ / 城市 / 州相关的错误
      if (Object.keys(errors).length) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated.zipCode;
          delete updated.city;
          delete updated.state;
          return updated;
        });
      }
    } else {
      // 邮编长度不足时，解锁 state/city，并清除之前的 PLZ 相关错误
      setIsLocationLocked(false);
      if (errors.zipCode || errors.city || errors.state) {
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated.zipCode;
          delete updated.city;
          delete updated.state;
          return updated;
        });
      }
    }
  };

  //输入框change
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;

    // 当用户输入任何内容时（包括空格），清除该字段的错误状态
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }

    setPaymentInfo({
      ...paymentInfo,
      [name]: value,
    });
  };

  // 统一的 blur 校验处理函数
  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // 标记该字段已被触碰
    setTouched((prev) => ({ ...prev, [name]: true }));

    switch (name) {
      case "phone": {
        if (!value) {
          delete newErrors.phone;
        } else {
          const ok = isValidPhoneSimple(
            value,
            paymentInfo.country || getDefaultCountryByLang(i18n.language)
          );
          if (!ok) {
            newErrors.phone = t("checkout.validation.phone.invalid");
          } else {
            delete newErrors.phone;
          }
        }
        break;
      }
      case "email":
        if (!value.trim()) {
          newErrors.email = t("checkout.validation.email.required");
        } else {
          const emailError = validateEmail(value.trim());
          if (emailError) {
            newErrors.email = emailError;
          } else {
            delete newErrors.email;
          }
        }
        break;

      case "firstName":
        if (!value.trim()) {
          newErrors.firstName = t("checkout.validation.firstName.required");
        } else {
          delete newErrors.firstName;
        }
        break;

      case "lastName":
        if (!value.trim()) {
          newErrors.lastName = t("checkout.validation.lastName.required");
        } else {
          delete newErrors.lastName;
        }
        break;

      case "zipCode":
        if (!value) {
          newErrors.zipCode = t("checkout.validation.zipCode.required");
        } else {
          const zipContent = value.replace(/[^a-zA-Z0-9]/g, ""); // 只保留字母和数字
          if (zipContent.length === 0) {
            newErrors.zipCode = t("checkout.validation.zipCode.invalid");
          } else {
            // 邮编验证通过，清除错误
            delete newErrors.zipCode;
          }
        }
        break;

      case "state":
        // state 字段是必填的
        if (!value) {
          newErrors.state = t("checkout.validation.state.required");
        } else {
          delete newErrors.state;
        }
        break;

      case "city":
        if (!value.trim()) {
          newErrors.city = t("checkout.validation.city.required");
        } else {
          delete newErrors.city;
        }
        break;

      case "address":
        if (!value.trim()) {
          newErrors.address = t("checkout.validation.address.required");
        } else {
          delete newErrors.address;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  // 国家或号码变更后，如用户已触碰 phone，则实时校验
  useEffect(() => {
    if (!touched?.phone) return;
    const ok = isValidPhoneSimple(
      paymentInfo.phone,
      paymentInfo.country || getDefaultCountryByLang(i18n.language)
    );
    setErrors((prev) => {
      const updated = { ...prev };
      if (!ok) {
        updated.phone = t("checkout.validation.phone.invalid");
      } else {
        delete updated.phone;
      }
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo.phone, paymentInfo.country, i18n.language]);

  //支付函数
  const handlePayNow = async () => {
    // 计算总价（仅购物车）
    const cartSubtotal = Array.isArray(cartItems)
      ? cartItems.reduce(
          (sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0),
          0
        )
      : 0;
    const cartShipping = shippingInfo.amount;
    const cartTotal = cartSubtotal + cartShipping;
    const userLang = (i18n.language || "nl").toLowerCase();
    const totalDueToday = isGbpLanguage(userLang)
      ? convertEurToGbp(cartTotal)
      : cartTotal;

    if (totalDueToday <= 0) {
      alert(t("checkout.validation.totalPositive"));
      return;
    }

    const { email, firstName, lastName, address, city, zipCode, state, phone } =
      paymentInfo;

    // 完整的字段校验
    const validationErrors = {};

    // 邮箱校验
    if (!email) {
      validationErrors.email = t("checkout.validation.email.required");
    } else {
      const emailError = validateEmail(email.trim());
      if (emailError) {
        validationErrors.email = emailError;
      }
    }

    // 电话号码校验：可选；若填写则按国家和位数校验
    if (phone) {
      const ok = isValidPhoneSimple(
        phone,
        paymentInfo.country || getDefaultCountryByLang(i18n.language)
      );
      if (!ok) {
        validationErrors.phone = t("checkout.validation.phone.invalid");
      }
    }

    // 姓名校验
    if (!firstName || !firstName.trim()) {
      validationErrors.firstName = t("checkout.validation.firstName.required");
    }
    if (!lastName || !lastName.trim()) {
      validationErrors.lastName = t("checkout.validation.lastName.required");
    }

    // 邮编校验
    if (!zipCode) {
      validationErrors.zipCode = t("checkout.validation.zipCode.required");
    } else {
      const zipContent = zipCode.replace(/[^a-zA-Z0-9]/g, "");
      if (zipContent.length === 0) {
        validationErrors.zipCode = t("checkout.validation.zipCode.invalid");
      }
    }

    // 州校验
    if (!state) {
      validationErrors.state = t("checkout.validation.state.required");
    }

    // 城市校验
    if (!city) {
      validationErrors.city = t("checkout.validation.city.required");
    } else if (!city.trim()) {
      validationErrors.city = t("checkout.validation.city.required");
    }

    // 地址校验
    if (!address) {
      validationErrors.address = t("checkout.validation.address.required");
    } else if (!address.trim()) {
      validationErrors.address = t("checkout.validation.address.required");
    }

    if (!agreements.research || !agreements.nonPrivate || !agreements.notMedicine || !agreements.terms) {
      setAgreementsError(true);
      validationErrors.agreements = "Please accept all required terms and conditions.";
    } else {
      setAgreementsError(false);
    }

    if (Object.keys(validationErrors).length > 0) {
      // 标记有错误的字段为已触碰，这样才会显示红色边框
      const newTouched = {};
      Object.keys(validationErrors).forEach((key) => {
        newTouched[key] = true;
      });
      setTouched((prev) => ({ ...prev, ...newTouched }));
      setErrors(validationErrors);
      try {
        const firstKey = Object.keys(validationErrors)[0];
        if (firstKey) {
          const el =
            document.querySelector(`[data-field="${firstKey}"]`) ||
            document.querySelector(".form__input--error");
          el && el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch {}
      return;
    }

    try {
      setIsLoading(true);

      // 检查 pixelRes 是否已加载
      if (!pixelRes) {
        alert(t("checkout.messages.paymentConfigLoading"));
        setIsLoading(false);
        return;
      }

      // 处理电话号码：根据国家前缀拼接，保留数字
      let phoneNumberOnly = phone ? phone.replace(/\D/g, "") : "";
      if (phoneNumberOnly) {
        const rule = getCountryPhoneRule(
          paymentInfo.country || getDefaultCountryByLang(i18n.language)
        );
        if (rule.code) {
          if (phoneNumberOnly.startsWith(rule.code)) {
            phoneNumberOnly = `+${phoneNumberOnly}`;
          } else {
            const national = phoneNumberOnly.replace(/^0+/, "");
            phoneNumberOnly = `+${rule.code}${national}`;
          }
        } else {
          phoneNumberOnly = `+${phoneNumberOnly}`;
        }
      }

      // 构建商品订单 goods 数据（电商场景）：含标题、SKU、价格、图片、数量、具体规格 options）
      const goodsCandidate =
        cartTotal > 0
          ? cartItems.map((item) => ({
              goods_title: item.title || "Unknown Product",
              goods_sku: Array.isArray(item.options) ? item.options : [],
              goods_price: isGbpLanguage(userLang)
                ? parseFloat(
                    convertEurToGbp(Number(item.price) || 0).toFixed(2)
                  )
                : Number(item.price) || 0,
              goods_image: item.thumb || item.image || "",
              goods_num: Number(item.qty) || 1,
            }))
          : null;

      if (!goodsCandidate) {
        alert("Your cart is empty.");
        setIsLoading(false);
        return;
      }

      // goods 可能为数组或对象，统一写入 localStorage 再取出（ensureGoods 内部会处理）
      const safeGoods = Array.isArray(goodsCandidate)
        ? (LocalStorageUtil.saveGoods(goodsCandidate), goodsCandidate)
        : LocalStorageUtil.ensureGoods(goodsCandidate, goodsCandidate);

      const goodsJson = JSON.stringify(safeGoods);

      const currencyName = isGbpLanguage(userLang) ? "GBP" : "EUR";

      let orderDetail = {
        ad: LocalStorageUtil.getAd() || "PurityBase",
        success_url: `${BASE_URL}/paysuccess?email=${encodeURIComponent(
          email.trim()
        )}&phone=${encodeURIComponent(phone.trim())}`,
        // success_url: "https://localhost:3000/paysuccess",
        cancel_url: `${BASE_URL}/payfial`,
        // cancel_url: "https://localhost:3000/payfial",
        back_url: `${BASE_URL}/checkout`, // 返回主页的URL
        locale: userLang, // 语言环境
        vehicleModel: "", // 产品型号
        host: window.location.host || "", // 当前域名
        headerLogo: "/head/head-new.png", // 头部Logo
        faviconLogo: "/head/ico-new.png", // 网站图标
        successLogo: "/main/main1.avif", // 成功页面Logo
        successText: "Thank you for your order!",
        indexTitle: "Puritybase | Secure Checkout",
        indexDesc:
          "Complete your purchase securely at Puritybase. High‑quality peptides with fast delivery and reliable service.",
        brandColor: " $shareColor", // 品牌颜色
        otherInfo: pixelRes?.groupid || "", // 其他信息
        img: "", // 不需要商品图片
        title: "Cart Order", // 订单名称
        // TODO:
        goods: goodsJson, //传入转json格式的对象
        payment_intent_id: "",
        type: 10,
        curcrency: "EUR", //货币固定为欧元
        email: email.trim(),
        phone: phoneNumberOnly || "",
        state: state || "", // 州
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        snCode: "",
        city: city || "",
        zipCode: zipCode || "",
        address: address || "",
        detailAddress: "",
        country: paymentInfo.country || "US", //根据语言修改国家
        id: "",
        orderNu: "",
        // TODO:
        tgid: pixelRes?.tgid || "",
        // 使用购物车第一个商品的 pixel/fbqtoken（商品专属配置优先）
        fbqToken: (() => {
          const firstId = cartItems[0]?.id;
          return firstId && productPixelMap[firstId]?.fbqtoken
            ? productPixelMap[firstId].fbqtoken
            : pixelRes?.fbqToken || "";
        })(),
        pixel: (() => {
          const firstId = cartItems[0]?.id;
          return firstId && productPixelMap[firstId]?.pixel
            ? productPixelMap[firstId].pixel
            : pixelRes?.pixel || "";
        })(),
        amount: parseFloat((parseFloat(totalDueToday) || 0).toFixed(2)),
        amount_fast: parseFloat((parseFloat(totalDueToday) || 0).toFixed(2)),
        // TODO:
        stusername: pixelRes?.stusername || "",
        user_login_name: Cookies.get("username") || "admin",
        channel: paymentMethod === "Klarna" ? "klarna" : null
      };

      // 构建商品事件数据（使用购物车项）
      const contentIds =
        Array.isArray(cartItems) && cartItems.length
          ? cartItems.map((it) => String(it.id || it.sku || it.title || "item"))
          : ["cart"];
      const contents =
        Array.isArray(cartItems) && cartItems.length
          ? cartItems.map((it) => ({
              id: String(it.id || it.sku || it.title || "item"),
              quantity: Number(it.qty) || 1,
              title: it.title || "Item",
            }))
          : [
              {
                id: "cart",
                quantity: 1,
                title: "CartOrder",
              },
            ];
      const numItems = contents.length;

      let userdata = {
        em: orderDetail.email,
        ph: orderDetail.phone,
        fn: orderDetail.firstName,
        ln: orderDetail.lastName,
        ct: orderDetail.city,
        st: orderDetail.state,
        zp: orderDetail.zipCode,
        country: orderDetail.country,
        external_id: "",
        client_ip_address: "",
        client_user_agent: "",
        fbc: "",
        fbp: "",
        lead_id: "",
        anon_id: "",
        madid: "",
        page_id: "",
        page_scoped_user_id: "",
        ctwa_clid: "",
        ig_account_id: "",
        ig_sid: "",
      };

      const eventData = {
        content_ids: contentIds,
        content_name: "Cart Order",
        value: parseFloat(totalDueToday),
        currency: currencyName,
        num_items: numItems,
        contents,
      };

      //getUrl
      const response = await getUrl(orderDetail);
      sendMessage("getUrl返回------- " + JSON.stringify(response));

      if (!pkMessageSentRef.current) {
        sendMessage(
          `${BASE_URL} [${totalDueToday} get url]:ad=${ad}:userid=${userid}`
        );
        // sendMessage(JSON.stringify(response.data.data));
        pkMessageSentRef.current = true;
      }

      const event_id = getEventId("InitiateCheckout");

      if (response.data.code === 1 && response.data.data) {
        // // // 直接发送邮件提醒

        // 在 getUrl 成功后调用 Pixel 和 Facebook API
        PixelWithEvent("InitiateCheckout", event_id);

        // 同时触发 Facebook Pixel 事件（使用购物车第一个商品的 pixel/fbqtoken）
        const firstProductId = cartItems[0]?.id;
        const firstProductPixel = firstProductId
          ? productPixelMap[firstProductId]
          : null;
        if (firstProductPixel?.pixel && firstProductPixel?.fbqtoken) {
          FacebookAPI.Facebook(
            firstProductPixel.fbqtoken,
            firstProductPixel.pixel,
            event_id,
            "AddPaymentInfo",
            eventData,
            userdata
          );
        }

        sendMessage(
          BASE_URL +
            `[${totalDueToday} InitiateCheckout]:ad=${ad}:userid=${userid}  `
        );
        let data = response.data.data;

        const orderNo = data.orderNo;
        if (orderNo && orderNo.trim() !== "") {
          localStorage.setItem("orderNo", orderNo);
          sendMessage(
            `准备跳转------- ${data.redirectlink}#/?orderNo=${orderNo}&service=st`
          );
          if (data.paymentType === 1) {
            window.location.href = data.redirectlink;
            return;
          }
          const redirectUrl = `${data.redirectlink}#/?orderNo=${orderNo}&service=st&isEmbedded=true`;
          // iframe 嵌入
          const initialHeight =
            typeof window !== "undefined" && window.innerHeight
              ? window.innerHeight
              : 720;
          setIframeUrl(redirectUrl);
          setShowIframe(true);
          setIframeHeight(initialHeight);
          // window.location.href = `${data.redirectlink}#/?orderNo=${orderNo}&service=st`;
        }
        // 保存用户填写的信息到 localStorage，供下次使用
        // localStorage.setItem("userPaymentInfo", JSON.stringify(paymentInfo));
        // const st_id = response.data.data.st_id;
        // setPublishableKey(response.data.data.api_publishable_key);
        // setShowPaymentForm(true);

        // setSuccessUrl(`${window.location.origin}/paysuccess`);

        // 保存购物订单概要供成功页使用
        const productInfo = {
          id: "cart",
          name: "Cart Order",
          price: parseFloat(totalDueToday),
          items:
            Array.isArray(cartItems) && cartItems.length
              ? cartItems.map((it) => ({
                  id: it.id || it.sku || it.title,
                  title: it.title,
                  qty: Number(it.qty) || 1,
                  price: Number(it.price) || 0,
                }))
              : [],
        };
        localStorage.setItem("product", JSON.stringify(productInfo));
      }
      // 保存訂單資訊到本地，成功頁與後續頁面可使用
      localStorage.setItem("orderDetail", JSON.stringify(orderDetail));
    } catch (error) {
      console.error("Payment Error:", error);

      // 改进错误信息显示
      let errorMessage = "Unknown error";
      if (error.response) {
        // 服务器返回了错误响应
        errorMessage = `Server error: ${error.response.status} - ${error.response.statusText}`;
        if (error.response.data) {
          errorMessage += ` - ${JSON.stringify(error.response.data)}`;
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        errorMessage =
          "Network error: No response from server. Please check your internet connection.";
      } else {
        // 其他错误
        errorMessage = error.message || String(error);
      }

      sendMessage(`getUrl报错------- ${errorMessage}`);
      sendMessage(
        `Error details: ${JSON.stringify({
          message: error.message,
          stack: error.stack,
          response: error.response
            ? {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
              }
            : null,
          request: error.request ? "Request sent but no response" : null,
        })}`
      );

      alert(t("checkout.errors.paymentError", { error: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const { items = [] } = useContext(CartContext) || {};
  const formatEuro = (n) => {
    const isUk = isGbpLanguage(i18n.language);
    const locale = isUk ? "en-GB" : "nl-NL";
    const currency = "EUR";
    const amount = n;
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace(/\u00A0/g, " ");
  };
  useEffect(() => {
    // 模拟一个小小的加载过程，刚进来时显示 "Calculating..."
    setShippingLoading(true);
    const timer = setTimeout(() => {
      setShippingLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [paymentInfo.country]);

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0),
        0
      ),
    [items]
  );
  const shippingInfo = useMemo(
    () => getShippingDisplay(items, i18n.language),
    [items, i18n.language]
  );
  const shippingAmount = shippingInfo.amount;
  const total = subtotal;

  return (
    <div>
      <Header />
      <main className="checkout-page">
        <div className="checkout__container">
          {/* <h1 className="checkout__title">{t("checkout.title")}</h1> */}
          <div className="checkout__layout">
            <div className="checkout__layout_left_both">
              <section
                className={`login-card${
                  loginLoading || regLoading ? " is-loading" : ""
                }`}
              >
                {loginError && !registerOpen && !loginSuccess ? (
                  <div className="login-alert login-alert--error">
                    {t("checkout.login.errorUnknownEmail")}
                  </div>
                ) : null}
                {regError && registerOpen ? (
                  <div className="login-alert login-alert--error">
                    {regError}
                  </div>
                ) : null}
                {!(loginSuccess || regSuccess) && (
                  <div className="login-card__head">
                    <div className="login-card__title">
                      {t("checkout.login.returning")}{" "}
                      <button
                        type="button"
                        className="login-card__link"
                        onClick={() => {
                          setRegisterOpen(false);
                          setLoginOpen((v) => !v);
                        }}
                        aria-expanded={loginOpen || undefined}
                      >
                        {t("checkout.login.clickToLogin")}
                      </button>
                    </div>
                  </div>
                )}
                {(loginOpen || loginSuccess) && !registerOpen && (
                  <div className="login-card__body">
                    {!loginSuccess ? (
                      <>
                        <p className="login-card__intro">
                          {t("checkout.login.intro1")}
                          <br />
                          {t("checkout.login.intro2")}
                        </p>
                        <form
                          className="login-form"
                          onSubmit={onLoginSubmit}
                          aria-busy={loginLoading || undefined}
                        >
                          <div className="login-form__row">
                            <div className="login-form__group">
                              <label className="form__label">
                                {t("checkout.labels.email")}
                              </label>
                              <input
                                className="form__input"
                                type="email"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                              />
                            </div>
                            <div className="login-form__group">
                              <label className="form__label">
                                {t("checkout.login.password")}
                              </label>
                              <input
                                className="form__input"
                                type="password"
                                value={loginPassword}
                                onChange={(e) =>
                                  setLoginPassword(e.target.value)
                                }
                              />
                            </div>
                            <button
                              className="login-form__btn"
                              type="submit"
                              disabled={loginLoading}
                            >
                              {t("checkout.login.loginBtn")}
                            </button>
                          </div>
                          <div className="login-form__row login-form__row--meta">
                            <button
                              type="button"
                              className="login-card__link"
                              onClick={() => {
                                setLoginOpen(false);
                                setRegisterOpen((v) => !v);
                              }}
                              aria-expanded={registerOpen || undefined}
                            >
                              {t("checkout.register.clickToRegister")}
                            </button>
                          </div>
                        </form>
                      </>
                    ) : (
                      <div className="success-card">
                        <div className="success-card-first-left">
                          <div className="success-card__avatar">
                            {(loginEmail || "U").charAt(0).toUpperCase()}
                          </div>
                          <div className="success-card__email">
                            {loginEmail}
                          </div>
                        </div>
                        <div className="success-card__menuwrap">
                          <button
                            className="success-card__menu"
                            type="button"
                            aria-label="menu"
                            onClick={() => setMenuOpen((v) => !v)}
                          >
                            ⋮
                          </button>
                          {menuOpen ? (
                            <div
                              className="menu-popover"
                              role="menu"
                              onClick={() => {
                                logoutUser();
                              }}
                            >
                              {tr("checkout.login.signOut", "sign out")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {registerOpen && (
                  <div className="login-card__body">
                    {!regSuccess ? (
                      <>
                        <p className="login-card__intro">
                          {t("checkout.register.intro")}
                        </p>
                        <form
                          className="login-form"
                          onSubmit={onRegisterSubmit}
                          aria-busy={regLoading || undefined}
                        >
                          <div className="login-form__row">
                            <div className="login-form__group">
                              <label className="form__label">
                                {t("checkout.labels.email")} <span>*</span>
                              </label>
                              <input
                                className="form__input"
                                type="email"
                                value={regEmail}
                                onChange={(e) => setRegEmail(e.target.value)}
                              />
                            </div>
                            <div className="login-form__group">
                              <label className="form__label">
                                {t("checkout.register.password")} <span>*</span>
                              </label>
                              <input
                                className="form__input"
                                type="password"
                                value={regPassword}
                                onChange={(e) => setRegPassword(e.target.value)}
                              />
                            </div>
                            <button
                              className="login-form__btn"
                              type="submit"
                              disabled={regLoading}
                            >
                              {t("checkout.register.registerBtn")}
                            </button>
                          </div>
                        </form>
                      </>
                    ) : (
                      <div className="success-card">
                        <div className="success-card__avatar">
                          {(regEmail || loginEmail || "U")
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="success-card__email">
                          {regEmail || loginEmail}
                        </div>
                        <div className="success-card__menuwrap">
                          <button
                            className="success-card__menu"
                            type="button"
                            aria-label="menu"
                            onClick={() => setMenuOpen((v) => !v)}
                          >
                            ⋮
                          </button>
                          {menuOpen ? (
                            <div
                              className="menu-popover"
                              role="menu"
                              onClick={() => {
                                logoutUser();
                              }}
                            >
                              {tr("checkout.login.signOut", "sign out")}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
              <section
                className="form-card form-card--contact"
                aria-label={t("checkout.aria.contact")}
              >
                <h2 className="form-card__sectionTitle">
                  {t("checkout.aria.contact")}
                </h2>
                <div className="form__grid form__grid--contact">
                  <div className="form__group form__full">
                    <label
                      htmlFor="cko-billing-email"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.email")}
                    </label>
                    <input
                      id="cko-billing-email"
                      className={`form__input${
                        touched?.email && errors?.email
                          ? " form__input--error"
                          : ""
                      }`}
                      type="email"
                      name="email"
                      value={paymentInfo.email}
                      onChange={handlePaymentChange}
                      onBlur={handleInputBlur}
                      data-field="email"
                      placeholder={t("checkout.labels.email")}
                      aria-invalid={
                        (touched?.email && !!errors?.email) || undefined
                      }
                    />
                    {touched?.email && errors?.email ? (
                      <div className="form__error">{errors.email}</div>
                    ) : null}
                  </div>
                </div>
              </section>

              <section
                className="form-card form-card--delivery"
                aria-label={t("checkout.aria.delivery")}
              >
                <h2 className="form-card__sectionTitle">
                  {t("checkout.aria.delivery")}
                </h2>
                <div className="form__grid">
                  <div
                    className="form__group form__full"
                    role="group"
                    aria-label={t("checkout.labels.country")}
                  >
                    <ReactFlagsSelect
                      selected={paymentInfo.selected || "US"}
                      onSelect={(code) =>
                        setPaymentInfo((prev) => ({
                          ...prev,
                          selected: code,
                          country: code,
                        }))
                      }
                      className="form__select"
                      placeholder={t("checkout.labels.country")}
                    />
                  </div>

                  <div className="form__group">
                    <label
                      htmlFor="cko-billing-firstName"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.firstName")}
                    </label>
                    <input
                      id="cko-billing-firstName"
                      className={`form__input${
                        touched?.firstName && errors?.firstName
                          ? " form__input--error"
                          : ""
                      }`}
                      type="text"
                      name="firstName"
                      value={paymentInfo.firstName}
                      onChange={handlePaymentChange}
                      onBlur={handleInputBlur}
                      data-field="firstName"
                      placeholder={t("checkout.labels.firstName")}
                      aria-invalid={
                        (touched?.firstName && !!errors?.firstName) || undefined
                      }
                    />
                    {touched?.firstName && errors?.firstName ? (
                      <div className="form__error">{errors.firstName}</div>
                    ) : null}
                  </div>
                  <div className="form__group">
                    <label
                      htmlFor="cko-billing-lastName"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.lastName")}
                    </label>
                    <input
                      id="cko-billing-lastName"
                      className={`form__input${
                        touched?.lastName && errors?.lastName
                          ? " form__input--error"
                          : ""
                      }`}
                      type="text"
                      name="lastName"
                      value={paymentInfo.lastName}
                      onChange={handlePaymentChange}
                      onBlur={handleInputBlur}
                      data-field="lastName"
                      placeholder={t("checkout.labels.lastName")}
                      aria-invalid={
                        (touched?.lastName && !!errors?.lastName) || undefined
                      }
                    />
                    {touched?.lastName && errors?.lastName ? (
                      <div className="form__error">{errors.lastName}</div>
                    ) : null}
                  </div>
                  <div className="form__group form__full">
                    <label
                      htmlFor="cko-billing-phone"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.phoneOptional")}
                    </label>
                    <PhoneInput
                      country={String(
                        paymentInfo.country || "us"
                      ).toLowerCase()}
                      value={paymentInfo.phone}
                      onChange={(val) => {
                        setPaymentInfo((prev) => ({ ...prev, phone: val }));
                        setTouched((prev) => ({ ...prev, phone: true }));
                      }}
                      enableLongNumbers
                      inputProps={{
                        id: "cko-billing-phone",
                        name: "phone",
                      }}
                      placeholder={t("checkout.labels.phoneOptional")}
                      inputClass="form__input phone-input"
                      buttonClass="phone-flag"
                      containerClass="phone-container"
                    />
                    {touched?.phone && errors?.phone ? (
                      <div className="form__error">{errors.phone}</div>
                    ) : null}
                  </div>
                  <div className="form__group form__full">
                    <label
                      htmlFor="cko-billing-zipCode"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.postalCode")}
                    </label>
                    <input
                      id="cko-billing-zipCode"
                      className={`form__input${
                        touched?.zipCode && errors?.zipCode
                          ? " form__input--error"
                          : ""
                      }`}
                      type="text"
                      name="zipCode"
                      value={paymentInfo.zipCode}
                      onChange={onPlzChange}
                      onBlur={handleInputBlur}
                      data-field="zipCode"
                      placeholder={t("checkout.labels.postalCode")}
                      aria-invalid={
                        (touched?.zipCode && !!errors?.zipCode) || undefined
                      }
                    />
                    {touched?.zipCode && errors?.zipCode ? (
                      <div className="form__error">{errors.zipCode}</div>
                    ) : null}
                  </div>

                  <div className="form__group">
                    <label
                      htmlFor="cko-billing-state"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.province")}
                    </label>
                    <input
                      id="cko-billing-state"
                      className={`form__input${
                        touched?.state && errors?.state
                          ? " form__input--error"
                          : ""
                      }`}
                      type="text"
                      name="state"
                      value={paymentInfo.state}
                      onChange={handlePaymentChange}
                      onBlur={handleInputBlur}
                      disabled={isLocationLocked}
                      data-field="state"
                      placeholder={t("checkout.labels.province")}
                      aria-invalid={
                        (touched?.state && !!errors?.state) || undefined
                      }
                    />
                    {touched?.state && errors?.state ? (
                      <div className="form__error">{errors.state}</div>
                    ) : null}
                  </div>
                  <div className="form__group">
                    <label
                      htmlFor="cko-billing-city"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.city")}
                    </label>
                    <input
                      id="cko-billing-city"
                      className={`form__input${
                        touched?.city && errors?.city
                          ? " form__input--error"
                          : ""
                      }`}
                      type="text"
                      name="city"
                      value={paymentInfo.city}
                      onChange={handlePaymentChange}
                      onBlur={handleInputBlur}
                      disabled={isLocationLocked}
                      data-field="city"
                      placeholder={t("checkout.labels.city")}
                      aria-invalid={
                        (touched?.city && !!errors?.city) || undefined
                      }
                    />
                    {touched?.city && errors?.city ? (
                      <div className="form__error">{errors.city}</div>
                    ) : null}
                  </div>
                  <div className="form__group form__full">
                    <label
                      htmlFor="cko-billing-address"
                      className="form__label form__label--srOnly"
                    >
                      {t("checkout.labels.street")}
                    </label>
                    <input
                      id="cko-billing-address"
                      className={`form__input${
                        touched?.address && errors?.address
                          ? " form__input--error"
                          : ""
                      }`}
                      type="text"
                      name="address"
                      value={paymentInfo.address}
                      onChange={handlePaymentChange}
                      onBlur={handleInputBlur}
                      data-field="address"
                      placeholder={t("checkout.labels.street")}
                      aria-invalid={
                        (touched?.address && !!errors?.address) || undefined
                      }
                    />
                    {touched?.address && errors?.address ? (
                      <div className="form__error">{errors.address}</div>
                    ) : null}
                  </div>
                </div>
                {showIframe && iframeUrl ? (
                  <div ref={iframeContainerRef} style={{ marginTop: 12 }}>
                    <div
                      className="payment-iframe"
                      style={{ height: iframeHeight }}
                    >
                      <iframe
                        title="checkout-iframe"
                        src={iframeUrl}
                        allow="payment;publickey-credentials-get; fullscreen; autoplay"
                        className="payment-iframe__frame"
                      />
                    </div>
                  </div>
                ) : null}
              </section>

              
              {/* payment */}
              <section className="agreements-card">
                <div className={`agreements-list ${agreementsError ? "has-error" : ""}`}>
                  <label className="agreement-item">
                    <input
                      type="checkbox"
                      checked={agreements.research}
                      onChange={(e) => setAgreements({ ...agreements, research: e.target.checked })}
                    />
                    <span className="agreement-text">
                      {t("checkout.agreements.research")} <span className="req">*</span>
                    </span>
                  </label>
                  <label className="agreement-item">
                    <input
                      type="checkbox"
                      checked={agreements.nonPrivate}
                      onChange={(e) => setAgreements({ ...agreements, nonPrivate: e.target.checked })}
                    />
                    <span className="agreement-text">
                      {t("checkout.agreements.nonPrivate")} <span className="req">*</span>
                    </span>
                  </label>
                  <label className="agreement-item">
                    <input
                      type="checkbox"
                      checked={agreements.notMedicine}
                      onChange={(e) => setAgreements({ ...agreements, notMedicine: e.target.checked })}
                    />
                    <span className="agreement-text">
                      {t("checkout.agreements.notMedicine")} <span className="req">*</span>
                    </span>
                  </label>
                  <label className="agreement-item">
                    <input
                      type="checkbox"
                      checked={agreements.terms}
                      onChange={(e) => setAgreements({ ...agreements, terms: e.target.checked })}
                    />
                    <span className="agreement-text">
                      {t("checkout.agreements.terms")} <span className="req">*</span>
                    </span>
                  </label> 
                </div>
                {agreementsError && (
                  <div className="form__error" style={{ marginTop: 8 }}>
                    {t("checkout.agreements.error")}
                  </div>
                )}
              </section>

              <section className="payment-card">
                <h2 className="payment-card__title">{t("checkout.payment.title")}</h2>
                <p className="payment-card__subtitle">{t("checkout.payment.subtitle")}</p>
                <div className="payment-methods">
                  <label className={`payment-method ${paymentMethod === "Klarna" ? "selected" : ""}`}>
                    <div className="payment-method__left">
                      <div className="radio-custom"></div>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Klarna"
                        checked={paymentMethod === "Klarna"}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="sr-only"
                      />
                      <span className="payment-method__name">{t("checkout.payment.klarna")}</span>
                    </div>
                  </label>
                  <label className={`payment-method ${paymentMethod === "Credit Card" ? "selected" : ""}`}>
                    <div className="payment-method__left">
                      <div className="radio-custom"></div>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Credit Card"
                        checked={paymentMethod === "Credit Card"}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="sr-only"
                      />
                      <span className="payment-method__name">{t("checkout.payment.creditCard")}</span>
                    </div>
                    <div className="payment-method__icons">
                      <img src="/checkout/visa.svg" alt="Visa" onError={(e) => e.target.style.display='none'} />
                      <img src="/checkout/mastercard.svg" alt="Mastercard" onError={(e) => e.target.style.display='none'} />
                    </div>
                  </label>
                </div>

                {!showIframe && (
                <button
                  className={`pay-btn pay-btn--gold${
                    isLoading ? " loading" : ""
                  }`}
                  onClick={() => {
                    handlePayNow();
                  }}
                  aria-busy={isLoading || undefined}
                  aria-disabled={!formIsValid || isLoading || undefined}
                  disabled={!formIsValid || isLoading}
                  type="button"
                >
                  {isLoading
                    ? tr("checkout.payNowLoading", "Processing...")
                    : t("checkout.payment.payNow")}
                </button>
              )}
                <div className="secure-badge">
                  <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
                    <path d="M11 6V5a3 3 0 0 0-6 0v1H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1zm-4 5a1 1 0 1 1 1-1 1 1 0 0 1-1 1zm2-5H7V5a1 1 0 0 1 2 0v1z"/>
                  </svg>
                  <span>{t("checkout.payment.secure")}</span>
                </div>
              </section>
            </div>

            <aside className="order-card" aria-label={t("checkout.aria.order")}>
              {items.map((it) => (
                <div
                  className="order-row"
                  key={
                    it?.lineKey ??
                    (it?.skuId != null ? `${it?.id}_${it.skuId}` : it?.id)
                  }
                >
                  <div className="col col-prod checkout_col order-row__full">
                    <div className="order-prod">
                      <div className="order-thumbWrap">
                        <img
                          className="order-thumb"
                          src={it.thumb || it.image || "/main/main1.avif"}
                          alt={it.title || ""}
                          loading="lazy"
                        />
                        <span
                          className="order-qtyBadge"
                          aria-label={`${t("checkout.order.qtyLabel")}: ${
                            Number(it.qty) || 0
                          }`}
                        >
                          {Number(it.qty) || 0}
                        </span>
                      </div>
                      <div className="order-info">
                        <div className="order-titleRow">
                          <div className="order-title">{it.title}</div>
                          <div className="order-linePrice">
                            {formatEuro(
                              (Number(it.price) || 0) * (Number(it.qty) || 0)
                            )}
                          </div>
                        </div>
                        {Array.isArray(it.options) &&
                          it.options.map((op, i) => (
                            <div className="order-opt" key={i}>
                              {op}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {items.length > 0 ? (
                <div className="order-discountBlock">
                  <div className="order-discountRow">
                    <input
                      type="text"
                      className="order-discountInput"
                      placeholder="Discount code"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value);
                        if (discountError) setDiscountError("");
                      }}
                    />
                    <button
                      type="button"
                      className="order-discountBtn"
                      onClick={() => {
                        if (!discountCode.trim()) return;
                        // 目前先模拟无效折扣码
                        setDiscountError("Invalid coupon code");
                      }}
                    >
                      Apply
                    </button>
                  </div>
                  {discountError && (
                    <div className="order-discountError">{discountError}</div>
                  )}
                </div>
              ) : null}
              <div className="order-sep" />
              <div className="order__row">
                <span>{t("checkout.order.subtotal")}</span>
                <strong>
                  {shippingInfo.formatted === "Free"
                    ? shippingInfo.formatted
                    : formatEuro(subtotal)}
                </strong>
              </div>
              <div className="order__row">
                <span>{t("checkout.order.shipping")}</span>
                <span className="order-shippingValue">
                  {shippingLoading ? "Calculating..." : "Select country"}
                </span>
              </div>
              <div className="order-sep" />
              <div className="order__row order__total">
                <span>{t("checkout.order.total")}</span>
                <div className="order__totalWrap">
                  <strong className="order__totalAmount">
                    {formatEuro(total)}
                  </strong>
                </div>
              </div>
              
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Checkout;
